/* eslint-disable max-lines */
import StepShell from '../StepShell';
import { colors, layout } from '../styles';
import usePreEmploymentDraft from './preEmployment/usePreEmploymentDraft';
import useSaveLaterHandler from './preEmployment/useSaveLaterHandler';
import { PreEmploymentFormData } from './preEmployment/PreEmploymentTypes';
import { BaseStepProps } from './BaseStepProps';

interface QuestionDef {
  key: keyof PreEmploymentFormData;
  text: string;
  explanationKey?: keyof PreEmploymentFormData;
  explanationLabel?: string;
}

const QUESTIONS: QuestionDef[] = [
  { key: 'atLeast18', text: 'Are you at least 18 years old?' },
  { key: 'legallyAbleToWork', text: 'Are you legally authorized to work in the United States?' },
  {
    key: 'licenseHeld',
    text: 'Have you ever held a state-issued professional license that has been revoked or suspended?',
  },
  {
    key: 'disciplinaryAction',
    text: 'Have you ever been the subject of disciplinary action by a licensing or regulatory agency?',
  },
  {
    key: 'abuseNeglect',
    text: 'Have you ever been the subject of an investigation involving alleged abuse or neglect of a child?',
    explanationKey: 'abuseNeglectExplanation',
    explanationLabel: 'Please explain',
  },
  {
    key: 'convictedOfCrime',
    text: 'Have you ever been convicted of a crime (excluding minor traffic violations)?',
    explanationKey: 'crimeExplanation',
    explanationLabel: 'Please describe (date, charge, disposition)',
  },
  {
    key: 'objectToFingerprinting',
    text: 'Do you have any objection to being fingerprinted as part of a background check?',
  },
  {
    key: 'thirdPartyAgreement',
    text: 'Do you authorize the company to obtain background, reference and credit information about you?',
  },
];

const PreEmploymentQuestionsStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  applicationDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const { formData, updateForm, saveAsDraft } = usePreEmploymentDraft(token, applicationDraft);
  const { handleSaveLater } = useSaveLaterHandler({
    saveAsDraft: () => saveAsDraft({ token, userId: tokenData.userId, userName: tokenData.userName }),
    onSaveLater,
  });

  const allAnswered = QUESTIONS.every((q) => typeof formData[q.key] === 'boolean');
  const allExplanationsFilled = QUESTIONS.every((q) => {
    if (!q.explanationKey) return true;
    if (formData[q.key] !== true) return true;
    const explanation = formData[q.explanationKey];
    return typeof explanation === 'string' && explanation.trim().length > 0;
  });
  const physicalAnswered = !!formData.physicalLimitations;
  const physicalExplanationFilled =
    formData.physicalLimitations !== 'yes' ||
    (typeof formData.physicalLimitationsExplanation === 'string' &&
      formData.physicalLimitationsExplanation.trim().length > 0);
  const canSubmit = allAnswered && allExplanationsFilled && physicalAnswered && physicalExplanationFilled;

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={onNext}
      nextLabel="Next"
      nextDisabled={!canSubmit}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Background Questions</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Answer truthfully. Falsification or omission may result in disqualification or dismissal.
      </p>

      {QUESTIONS.map((q) => {
        const value = formData[q.key];
        const showExplanation =
          q.explanationKey && (q.key === 'abuseNeglect' || q.key === 'convictedOfCrime') && value === true;
        return (
          <div key={String(q.key)} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <span style={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>{q.text}</span>
              <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={String(q.key)}
                    checked={value === true}
                    onChange={() => updateForm({ [q.key]: true } as Partial<PreEmploymentFormData>)}
                  />
                  Yes
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={String(q.key)}
                    checked={value === false}
                    onChange={() => updateForm({ [q.key]: false } as Partial<PreEmploymentFormData>)}
                  />
                  No
                </label>
              </div>
            </div>
            {showExplanation && q.explanationKey && (
              <div style={{ marginTop: 8 }}>
                <label style={layout.label}>{q.explanationLabel}</label>
                <textarea
                  rows={3}
                  value={String(formData[q.explanationKey] || '')}
                  onChange={(e) =>
                    updateForm({ [q.explanationKey as string]: e.target.value } as Partial<PreEmploymentFormData>)
                  }
                  style={{ ...layout.input, resize: 'vertical' }}
                />
              </div>
            )}
          </div>
        );
      })}

      <hr style={layout.divider} />

      <h4 style={{ fontSize: 14, fontWeight: 700, margin: '12px 0 8px' }}>Physical Limitations</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Any physical limitations? *</label>
          <select
            value={formData.physicalLimitations}
            onChange={(e) => updateForm({ physicalLimitations: e.target.value })}
            style={layout.select}
          >
            <option value="">Select...</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div>
          <label style={layout.label}>If yes, please explain {formData.physicalLimitations === 'yes' ? '*' : ''}</label>
          <input
            type="text"
            value={formData.physicalLimitationsExplanation}
            onChange={(e) => updateForm({ physicalLimitationsExplanation: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>
    </StepShell>
  );
};

export default PreEmploymentQuestionsStep;
